import type { SynthPreset } from "../types";

/**
 * Modulation pack — voices that lean on osc3, dedicated noise, and FM/ring.
 * Built for tracker writing: short gates, clear midrange, and a few deep pads
 * that still cut when you leave them running under a lead.
 */
export const MODULATION_PRESETS: readonly SynthPreset[] = [
  // Three hard-detuned saws through a mild filter — the JP-8000 supersaw gesture
  // on a mono voice. Slight LFO on cutoff so the cloud breathes under held notes.
  {
    name: "Chrome Supersaw",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -12, level: 0.34 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 14, level: 0.32 },
    osc3: { wave: "saw", octave: 0, semis: 0, detuneCents: -3, level: 0.3 },
    filter: { type: "lowpass", cutoffHz: 4200, resonance: 0.14 },
    ampEnv: { a: 0.01, d: 0.25, s: 0.78, r: 0.35 },
    modEnv: { a: 0.001, d: 0.4, s: 0.2, r: 0.2 },
    lfo: { rateHz: 0.22, wave: "tri", depth: 0.45 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.7 },
      { source: "lfo", target: "cutoff", depth: 0.55 },
    ],
    glideMs: 18,
  },

  // Classic two-operator glass: sine carrier, silent sine modulator an octave up,
  // modEnv peels the FM index off so the strike rings then settles into a pure tone.
  {
    name: "Glass Carillon",
    osc1: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0.55 },
    osc2: { wave: "sine", octave: 2, semis: 0, detuneCents: 4, level: 0.12 },
    osc3: { wave: "sine", octave: 2, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "osc1", fmDepth: 0.15, amDepth: 0, velocityToFm: 0.55 },
    filter: { type: "lowpass", cutoffHz: 9000, resonance: 0.08 },
    ampEnv: { a: 0.001, d: 1.4, s: 0, r: 0.9 },
    modEnv: { a: 0.001, d: 0.55, s: 0, r: 0.25 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 2.4 },
      { source: "ampEnv", target: "cutoff", depth: 0.4 },
    ],
  },

  // Heavier temple-bell: saw modulator for inharmonic grit, short noise chiff,
  // high velocity→FM so soft hits stay pure and hard ones crash.
  {
    name: "Temple Bell",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0.5 },
    osc2: { wave: "tri", octave: 1, semis: 5, detuneCents: -6, level: 0.1 },
    osc3: { wave: "saw", octave: 1, semis: 4, detuneCents: 0, level: 0 },
    noise: { level: 0.07 },
    fm: { target: "osc1", fmDepth: 0.4, amDepth: 0, velocityToFm: 0.75 },
    filter: { type: "bandpass", cutoffHz: 2400, resonance: 0.35 },
    ampEnv: { a: 0.001, d: 1.8, s: 0, r: 1.1 },
    modEnv: { a: 0.001, d: 0.18, s: 0, r: 0.1 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 3.2 },
      { source: "modEnv", target: "cutoff", depth: 1.4 },
      { source: "ampEnv", target: "amp", depth: -0.15 },
    ],
  },

  // DX-leaning electric-piano pluck: FM bloom on the attack, then a soft sine body.
  {
    name: "Ivory Operator",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: -2, level: 0.48 },
    osc2: { wave: "sine", octave: 1, semis: 0, detuneCents: 3, level: 0.16 },
    osc3: { wave: "sine", octave: 2, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "both", fmDepth: 0.2, amDepth: 0, velocityToFm: 0.65 },
    filter: { type: "lowpass", cutoffHz: 5200, resonance: 0.12 },
    ampEnv: { a: 0.002, d: 0.85, s: 0.18, r: 0.45 },
    modEnv: { a: 0.001, d: 0.22, s: 0, r: 0.12 },
    lfo: { rateHz: 4.6, wave: "sine", depth: 0.25 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 2.8 },
      { source: "lfo", target: "pitch", depth: 0.06 },
    ],
  },

  // Wire-harp pluck: narrow pulse carriers with a little ring for the metallic edge.
  {
    name: "Wire Harp",
    osc1: { wave: "square", octave: 1, semis: 0, detuneCents: 0, level: 0.38, pwm: 0.18 },
    osc2: { wave: "square", octave: 1, semis: 7, detuneCents: -8, level: 0.22, pwm: 0.3 },
    osc3: { wave: "sine", octave: 2, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "both", fmDepth: 0.35, amDepth: 0.22, velocityToFm: 0.4 },
    filter: { type: "highpass", cutoffHz: 280, resonance: 0.2 },
    ampEnv: { a: 0.001, d: 0.55, s: 0, r: 0.2 },
    modEnv: { a: 0.001, d: 0.12, s: 0, r: 0.06 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 1.6 },
      { source: "modEnv", target: "amDepth", depth: 0.35 },
    ],
  },

  // Deep sonar ping: sub sine with slow-decaying FM sparkle — good for sparse tracker bass.
  {
    name: "Submarine Sonar",
    osc1: { wave: "sine", octave: -2, semis: 0, detuneCents: 0, level: 0.62 },
    osc2: { wave: "sine", octave: -1, semis: 0, detuneCents: 2, level: 0.2 },
    osc3: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "osc1", fmDepth: 0.05, amDepth: 0, velocityToFm: 0.3 },
    filter: { type: "lowpass", cutoffHz: 900, resonance: 0.25 },
    ampEnv: { a: 0.004, d: 0.9, s: 0.55, r: 0.5 },
    modEnv: { a: 0.001, d: 0.45, s: 0.1, r: 0.3 },
    lfo: { rateHz: 0.15, wave: "sine", depth: 0.3 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 1.8 },
      { source: "modEnv", target: "cutoff", depth: 1.6 },
      { source: "lfo", target: "cutoff", depth: 0.4 },
    ],
    glideMs: 40,
  },

  // Sustained iron pad: light FM on both carriers plus a breath of noise.
  {
    name: "Iron Lattice",
    osc1: { wave: "tri", octave: 0, semis: 0, detuneCents: -8, level: 0.36 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 10, level: 0.28 },
    osc3: { wave: "sine", octave: 1, semis: 7, detuneCents: 0, level: 0.05 },
    noise: { level: 0.04 },
    fm: { target: "both", fmDepth: 0.55, amDepth: 0.08, velocityToFm: 0.2 },
    filter: { type: "lowpass", cutoffHz: 1800, resonance: 0.22 },
    ampEnv: { a: 0.35, d: 0.6, s: 0.82, r: 1.4 },
    modEnv: { a: 0.2, d: 1.2, s: 0.35, r: 1 },
    lfo: { rateHz: 0.18, wave: "tri", depth: 0.5 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 0.9 },
      { source: "lfo", target: "cutoff", depth: 0.7 },
      { source: "lfo", target: "amDepth", depth: 0.15 },
    ],
  },

  // Aggressive mono lead: FM into both carriers, modEnv scrapes index on every hit.
  {
    name: "Phase Dagger",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -6, level: 0.4 },
    osc2: { wave: "square", octave: 0, semis: 0, detuneCents: 7, level: 0.22, pwm: 0.35 },
    osc3: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "both", fmDepth: 0.8, amDepth: 0, velocityToFm: 0.5 },
    filter: { type: "lowpass", cutoffHz: 2600, resonance: 0.4 },
    ampEnv: { a: 0.004, d: 0.28, s: 0.55, r: 0.18 },
    modEnv: { a: 0.001, d: 0.16, s: 0.1, r: 0.08 },
    lfo: { rateHz: 5.5, wave: "sine", depth: 0.35 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 2.2 },
      { source: "modEnv", target: "cutoff", depth: 2 },
      { source: "lfo", target: "pitch", depth: 0.12 },
    ],
    glideMs: 28,
  },

  // Full ring-mod lead: carriers nulled into sidebands — play fifths and octaves for classic clang.
  {
    name: "Ring Spire",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0.5 },
    osc2: { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0 },
    osc3: { wave: "sine", octave: 0, semis: 7, detuneCents: 0, level: 0 },
    fm: { target: "osc1", fmDepth: 0, amDepth: 1, velocityToFm: 0 },
    filter: { type: "bandpass", cutoffHz: 1600, resonance: 0.28 },
    ampEnv: { a: 0.008, d: 0.35, s: 0.6, r: 0.25 },
    modEnv: { a: 0.001, d: 0.3, s: 0.2, r: 0.15 },
    lfo: { rateHz: 0.4, wave: "tri", depth: 0.4 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 1.2 },
      { source: "lfo", target: "cutoff", depth: 0.6 },
    ],
    glideMs: 12,
  },

  // Soft chip-bell for arps: tiny FM hit, almost no sustain — thrives under Cxx gates.
  {
    name: "Chip Bell",
    osc1: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0.52 },
    osc2: { wave: "tri", octave: 2, semis: 0, detuneCents: 5, level: 0.1 },
    osc3: { wave: "sine", octave: 3, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "osc1", fmDepth: 0.1, amDepth: 0, velocityToFm: 0.45 },
    filter: { type: "lowpass", cutoffHz: 7800, resonance: 0.1 },
    ampEnv: { a: 0.001, d: 0.22, s: 0, r: 0.1 },
    modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.04 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 1.8 },
    ],
  },

  // Thick tracker choir: three sines stacked with a whisper of noise and slow chorus-like LFO.
  {
    name: "Tracker Choir",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: -7, level: 0.34 },
    osc2: { wave: "sine", octave: 0, semis: 0, detuneCents: 9, level: 0.3 },
    osc3: { wave: "tri", octave: 1, semis: 0, detuneCents: -2, level: 0.18 },
    noise: { level: 0.025 },
    filter: { type: "lowpass", cutoffHz: 2800, resonance: 0.12 },
    ampEnv: { a: 0.12, d: 0.4, s: 0.85, r: 0.8 },
    modEnv: { a: 0.2, d: 0.8, s: 0.4, r: 0.6 },
    lfo: { rateHz: 0.35, wave: "sine", depth: 0.55 },
    routes: [
      { source: "lfo", target: "pitch", depth: 0.08 },
      { source: "lfo", target: "amp", depth: 0.08 },
      { source: "modEnv", target: "cutoff", depth: 0.9 },
    ],
  },

  // DnB-adjacent grit: detuned saws with a quiet FM modulator chewing the midrange.
  {
    name: "Void Reese",
    osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: -16, level: 0.38 },
    osc2: { wave: "saw", octave: -1, semis: 0, detuneCents: 18, level: 0.36 },
    osc3: { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "both", fmDepth: 0.45, amDepth: 0.05, velocityToFm: 0.15 },
    filter: { type: "lowpass", cutoffHz: 320, resonance: 0.32 },
    ampEnv: { a: 0.025, d: 0.5, s: 0.82, r: 0.35 },
    modEnv: { a: 0.01, d: 0.5, s: 0.3, r: 0.25 },
    lfo: { rateHz: 0.28, wave: "tri", depth: 0.55 },
    routes: [
      { source: "lfo", target: "cutoff", depth: 0.85 },
      { source: "modEnv", target: "fmDepth", depth: 0.7 },
      { source: "lfo", target: "amDepth", depth: 0.12 },
    ],
    glideMs: 35,
  },

  // Rusted industrial hit: noise-heavy body, ring-mod scrape, short amp.
  {
    name: "Rusted Gate",
    osc1: { wave: "square", octave: -1, semis: 0, detuneCents: 0, level: 0.28, pwm: 0.4 },
    osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 11, level: 0.18 },
    osc3: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0 },
    noise: { level: 0.35 },
    fm: { target: "both", fmDepth: 0, amDepth: 0.7, velocityToFm: 0 },
    filter: { type: "bandpass", cutoffHz: 900, resonance: 0.45 },
    ampEnv: { a: 0.001, d: 0.18, s: 0, r: 0.12 },
    modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.04 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 2.4 },
      { source: "modEnv", target: "amDepth", depth: 0.4 },
    ],
  },

  // Soft FM brass: slow attack, mid FM index, osc3 also faintly audible as a fifth.
  {
    name: "Orbital Brass",
    osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: -5, level: 0.4 },
    osc2: { wave: "square", octave: 0, semis: 0, detuneCents: 6, level: 0.2, pwm: 0.42 },
    osc3: { wave: "sine", octave: 0, semis: 7, detuneCents: 0, level: 0.12 },
    fm: { target: "osc1", fmDepth: 0.6, amDepth: 0, velocityToFm: 0.35 },
    filter: { type: "lowpass", cutoffHz: 1600, resonance: 0.28 },
    ampEnv: { a: 0.08, d: 0.35, s: 0.7, r: 0.3 },
    modEnv: { a: 0.06, d: 0.5, s: 0.35, r: 0.25 },
    lfo: { rateHz: 5.2, wave: "sine", depth: 0.3 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 1.1 },
      { source: "modEnv", target: "cutoff", depth: 1.5 },
      { source: "lfo", target: "pitch", depth: 0.1 },
    ],
    glideMs: 22,
  },

  // Paper-light pad: sine body, noise chiff on attack via ampEnv→cutoff, tiny FM shimmer.
  {
    name: "Paper Crane",
    osc1: { wave: "sine", octave: 1, semis: 0, detuneCents: -3, level: 0.42 },
    osc2: { wave: "tri", octave: 1, semis: 0, detuneCents: 5, level: 0.18 },
    osc3: { wave: "sine", octave: 3, semis: 0, detuneCents: 0, level: 0 },
    noise: { level: 0.06 },
    fm: { target: "osc1", fmDepth: 0.12, amDepth: 0, velocityToFm: 0.25 },
    filter: { type: "highpass", cutoffHz: 220, resonance: 0.15 },
    ampEnv: { a: 0.04, d: 0.7, s: 0.45, r: 0.9 },
    modEnv: { a: 0.001, d: 0.15, s: 0, r: 0.1 },
    lfo: { rateHz: 0.25, wave: "sine", depth: 0.35 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 1.2 },
      { source: "ampEnv", target: "cutoff", depth: 0.8 },
      { source: "lfo", target: "amp", depth: 0.1 },
    ],
  },

  // Crystal shard — bright, short, velocity-sensitive FM for melodic fills.
  {
    name: "Crystal Shard",
    osc1: { wave: "sine", octave: 2, semis: 0, detuneCents: 0, level: 0.45 },
    osc2: { wave: "sine", octave: 3, semis: 0, detuneCents: -4, level: 0.14 },
    osc3: { wave: "tri", octave: 3, semis: 4, detuneCents: 0, level: 0 },
    fm: { target: "both", fmDepth: 0.25, amDepth: 0.1, velocityToFm: 0.8 },
    filter: { type: "highpass", cutoffHz: 600, resonance: 0.18 },
    ampEnv: { a: 0.001, d: 0.4, s: 0, r: 0.25 },
    modEnv: { a: 0.001, d: 0.1, s: 0, r: 0.05 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 2.6 },
      { source: "modEnv", target: "amDepth", depth: 0.3 },
    ],
  },

  // Dark ambient bed: three-osc stack with slow LFO on FM depth — leave it under a melody.
  {
    name: "Neon Abyss",
    osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: -10, level: 0.3 },
    osc2: { wave: "tri", octave: 0, semis: 0, detuneCents: 8, level: 0.26 },
    osc3: { wave: "sine", octave: 1, semis: 3, detuneCents: 0, level: 0.1 },
    noise: { level: 0.03 },
    fm: { target: "osc2", fmDepth: 0.35, amDepth: 0.12, velocityToFm: 0 },
    filter: { type: "lowpass", cutoffHz: 700, resonance: 0.2 },
    ampEnv: { a: 0.8, d: 1, s: 0.9, r: 2 },
    modEnv: { a: 1.2, d: 2, s: 0.5, r: 2 },
    lfo: { rateHz: 0.08, wave: "sine", depth: 0.7 },
    routes: [
      { source: "lfo", target: "fmDepth", depth: 0.9 },
      { source: "lfo", target: "cutoff", depth: 0.8 },
      { source: "modEnv", target: "cutoff", depth: 0.6 },
    ],
  },

  // Percussive FM tom / body hit for tracker drums when you need pitched metal.
  {
    name: "Cavern Tom FM",
    osc1: { wave: "sine", octave: -1, semis: 0, detuneCents: 0, level: 0.55 },
    osc2: { wave: "tri", octave: 0, semis: 0, detuneCents: 0, level: 0.15 },
    osc3: { wave: "sine", octave: 1, semis: 0, detuneCents: 0, level: 0 },
    noise: { level: 0.12 },
    fm: { target: "osc1", fmDepth: 0.3, amDepth: 0, velocityToFm: 0.4 },
    filter: { type: "lowpass", cutoffHz: 1200, resonance: 0.35 },
    ampEnv: { a: 0.001, d: 0.28, s: 0, r: 0.18 },
    modEnv: { a: 0.001, d: 0.12, s: 0, r: 0.06 },
    lfo: { rateHz: 0, wave: "sine", depth: 0 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 2.5 },
      { source: "modEnv", target: "pitch", depth: -8 },
      { source: "modEnv", target: "cutoff", depth: 2.2 },
    ],
  },

  // Soft operator pad for chords across multiple columns — gentle FM, long release.
  {
    name: "Soft Operator",
    osc1: { wave: "sine", octave: 0, semis: 0, detuneCents: -4, level: 0.4 },
    osc2: { wave: "sine", octave: 1, semis: 0, detuneCents: 5, level: 0.22 },
    osc3: { wave: "sine", octave: 2, semis: 0, detuneCents: 0, level: 0 },
    fm: { target: "both", fmDepth: 0.18, amDepth: 0, velocityToFm: 0.3 },
    filter: { type: "lowpass", cutoffHz: 3400, resonance: 0.1 },
    ampEnv: { a: 0.2, d: 0.5, s: 0.75, r: 1.2 },
    modEnv: { a: 0.15, d: 0.9, s: 0.3, r: 0.8 },
    lfo: { rateHz: 0.3, wave: "sine", depth: 0.4 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 0.7 },
      { source: "lfo", target: "cutoff", depth: 0.45 },
      { source: "lfo", target: "pitch", depth: 0.05 },
    ],
  },

  // Deep well — sub + audible osc3 fifth + light ring for hollow tracker bass.
  {
    name: "Deep Well",
    osc1: { wave: "sine", octave: -2, semis: 0, detuneCents: 0, level: 0.5 },
    osc2: { wave: "tri", octave: -1, semis: 0, detuneCents: 3, level: 0.22 },
    osc3: { wave: "sine", octave: 0, semis: 7, detuneCents: 0, level: 0.14 },
    fm: { target: "osc1", fmDepth: 0.2, amDepth: 0.18, velocityToFm: 0.2 },
    filter: { type: "lowpass", cutoffHz: 480, resonance: 0.22 },
    ampEnv: { a: 0.015, d: 0.35, s: 0.75, r: 0.28 },
    modEnv: { a: 0.005, d: 0.3, s: 0.25, r: 0.2 },
    lfo: { rateHz: 0.2, wave: "tri", depth: 0.35 },
    routes: [
      { source: "modEnv", target: "fmDepth", depth: 0.8 },
      { source: "modEnv", target: "cutoff", depth: 1.1 },
      { source: "lfo", target: "amDepth", depth: 0.2 },
    ],
    glideMs: 50,
  },
];
